/**
 * TimesheetSummaryPanel
 * Shows monthly timesheet stats: KPIs + admin type breakdown
 * GET /api/admin/timesheets/stats/?date_from=&date_to=
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Clock, ChevronLeft, ChevronRight, RefreshCw, Loader2, CheckCircle2, XCircle, Timer, Users } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const isoMonth = (y, m) => {
  const from = `${y}-${String(m).padStart(2, '0')}-01`;
  const last  = new Date(y, m, 0).getDate();
  const to    = `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { from, to };
};

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const Kpi = ({ icon: Icon, label, value, color, sub }) => (
  <div className="flex items-start gap-2 p-2 rounded-xl border"
    style={{ backgroundColor: `${color}0D`, borderColor: `${color}25` }}>
    <div className="p-1.5 rounded-lg flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
      <Icon size={12} style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-[9px] text-slate-400 font-medium uppercase tracking-wide leading-none mb-0.5">{label}</p>
      <p className="text-sm font-bold leading-none" style={{ color: value > 0 ? color : '#1e293b' }}>{value ?? '—'}</p>
      {sub && <p className="text-[9px] text-slate-400 mt-0.5 leading-none">{sub}</p>}
    </div>
  </div>
);

const TimesheetSummaryPanel = () => {
  const now = new Date();
  const [year,    setYear]    = useState(now.getFullYear());
  const [month,   setMonth]   = useState(now.getMonth() + 1);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    const { from, to } = isoMonth(year, month);
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    axios.get(`${API_BASE}/api/admin/timesheets/stats/`, { headers, params: { date_from: from, date_to: to } })
      .then(r => setData(r.data))
      .catch(() => setError('Could not load timesheet stats.'))
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">

      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <Clock size={13} className="text-white" />
          </div>
          <span className="text-sm font-semibold text-slate-800">Timesheet Summary</span>
          {!loading && data && data.pending > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600">
              {data.pending} pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prevMonth} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
            <ChevronLeft size={13} />
          </button>
          <span className="text-[11px] font-semibold text-slate-600 w-14 text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button onClick={nextMonth} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
            <ChevronRight size={13} />
          </button>
          <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors ml-1">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && data && (
        <div className="grid grid-cols-2 gap-1.5 p-2.5">
          <Kpi icon={Users}       label="Total"    value={data.total}    color="#3B82F6" sub={data.total_hours} />
          <Kpi icon={Timer}       label="Pending"  value={data.pending}  color="#F59E0B" />
          <Kpi icon={CheckCircle2}label="Approved" value={data.approved} color="#10B981" sub={data.approved_hours} />
          <Kpi icon={XCircle}     label="Rejected" value={data.rejected} color="#EF4444" />
        </div>
      )}
    </div>
  );
};

export default TimesheetSummaryPanel;
